import { useEffect, useRef } from "react";

/**
 * Tracks how long a referenced element stays visible in the viewport and
 * reports the accumulated seconds through `onReport` whenever the element
 * leaves the viewport, the tab is hidden, or the component unmounts.
 * @param {(seconds: number) => void} onReport
 * @param {{ threshold?: number, minSeconds?: number }} options
 * @returns {React.RefObject}
 */
export function useVisibleTime(onReport, { threshold = 0.5, minSeconds = 2 } = {}) {
  const ref = useRef(null);
  const onReportRef = useRef(onReport);

  useEffect(() => {
    onReportRef.current = onReport;
  }, [onReport]);

  useEffect(() => {
    const element = ref.current;
    if (!element || typeof IntersectionObserver === "undefined") return;

    let visibleSince = null;
    let accumulated = 0;

    const flush = () => {
      if (visibleSince !== null) {
        accumulated += (performance.now() - visibleSince) / 1000;
        visibleSince = null;
      }
    };

    const report = () => {
      flush();
      const seconds = Math.round(accumulated);
      if (seconds >= minSeconds) {
        onReportRef.current(seconds);
        accumulated = 0;
      }
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        if (entry.isIntersecting) {
          if (visibleSince === null) visibleSince = performance.now();
        } else {
          report();
        }
      },
      { threshold },
    );

    observer.observe(element);

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") report();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("pagehide", report);

    return () => {
      report();
      observer.disconnect();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("pagehide", report);
    };
  }, [threshold, minSeconds]);

  return ref;
}
